import { useState, useEffect } from 'react';
import {
  Button,
  SkeletonPlaceholder,
  Toggle,
  Slider,
  Tooltip,
  ModalWrapper,
} from 'carbon-components-react';
import { FormModal } from 'src/common/utils/FormModal';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../locales/messages';
import Cookies from 'js-cookie';
import Helmet from 'react-helmet';
import './StorageProtectCalculator.scss';
import '../../../common/style/importantStyle.scss';
import '../../../common/style/common.scss';
import countryList from '../services/currencies.json';
import { pageTitle, segmentTracking } from '../lib/tracker';
import Footnote from './Footnote/Footnote';
import Thanks from './Thanks/Thanks';
import { useEnv } from '../../../common/hooks/widgets/useEnv';
import { useBPName } from '../../../common/hooks/widgets/useBPName';
import { useCookiesURL } from '../../../common/hooks/widgets/useCookiesURL';
import { usePrice } from '../hooks/usePrice';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import * as URX_FORM from '../constants/urx-form';
import {
  LangDefaults,
  determineCountry,
  useLang,
} from 'src/common/hooks/widgets/useLang';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import {
  urxScript,
  urxScriptLoader,
} from 'src/common/hooks/widgets/globalDefinitions';
import BusinessPartner from 'src/common/components/BusinessPartner/BusinessPartner';
import {
  UT30StorageProtect,
  location,
  object,
  pageUrl,
  productTitle,
} from '../constants/spread';
import { ArrowRight16, Edit16 } from '@carbon/icons-react';
import {
  CUSTOMISED_QUOTE,
  CloudD365,
  CloudEntra,
  CloudGW,
  CloudM365,
  CloudSalesforce,
} from '../constants/prices';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import DisclaimerText from 'src/common/components/DisclaimerText/DisclaimerText';
import { shortcode } from '../StorageProtectCalculator.widget';

export interface StorageProtectCalculatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const StorageProtectCalculator = ({
  langCode,
  test,
  countryToTest,
}: StorageProtectCalculatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');

  // lang and userlocale is based out of ddo
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);

  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');

  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound === null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  /* Have urx form deault in us-en when on language us page */
  const [urxlang] = useState(localeCode.toString());
  const [environment] = useEnv();
  const intl = useIntl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { bpName, logo, companyId, desc, mssp } = useBPName(
    UT30StorageProtect,
    true,
  );
  const cookieURL = useCookiesURL(shortcode);
  const [formId, setFormId] = useState('');
  const [formComplete, setFormComplete] = useState(false);
  const [bpConsentMail, setBpConsentMail] = useState(false);
  const isSmall = useSmallBreakpoint('560px');

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccessMultiple =
    window.onUrxFormSubmitSuccessMultiple || {};
  window.onUrxFormSubmitSuccessMultiple[URX_FORM.INSTANCE_ID] = function (
    param,
  ) {
    setFormComplete(true);
  };

  const [m365, setM365] = useState(CloudM365.DEFAULT);
  const [entra, setEntra] = useState(CloudEntra.DEFAULT);
  const [salesforce, setSalesforce] = useState(CloudSalesforce.DEFAULT);
  const [d365, setD365] = useState(CloudD365.DEFAULT);
  const [gw, setGw] = useState(CloudGW.DEFAULT);
  const [storage, setStorage] = useState(true);

  const [
    totalCost,
    m365Price,
    entraPrice,
    sfPrice,
    d365Price,
    gwPrice,
    totalCostRaw,
  ] = usePrice({
    currency,
    localeCode,
    country,
    m365,
    entra,
    salesforce,
    d365,
    gw,
    storage,
  });

  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  useEffect(() => {
    setCookie();
  }, [
    m365,
    m365Price,
    entra,
    entraPrice,
    sfPrice,
    salesforce,
    gw,
    gwPrice,
    formId,
  ]);

  const setCookie = () => {
    const mailCheckbox =
      document.querySelector<HTMLInputElement>('#Q_MAIL_CONSENT');
    setBpConsentMail(!!mailCheckbox?.checked); // set false if unchecked or if undefined
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: formId,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            `SP4C M365: Users=${m365},Price=${m365Price}` +
            ` | SP4C Entra ID: Users=${entra},Price=${entraPrice}` +
            ` | SP4C Salesforce: Users=${salesforce},Price=${sfPrice}` +
            ` | SP4C Dynamics: Users=${d365},Price=${d365Price}` +
            ` | SP4C GW: Users=${gw},Price=${gwPrice}` +
            ` | Estimated Monthly Cost - ${totalCost}` +
            (getBp() ? `|Preferred BP:${getBp()}` : ''),
          Q_ASSET_URL: window.location.href, // ISC - Asset URL initial
          Q_ASSET_NAME: 'Storage Protect For Cloud', // ISC - Asset Name
        },
      }),
    );
    console.log(Cookies.get('urxdcq'));
  };

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };

  /**
   * Handlers to check max and min and set value
   */
  const handleSliders = (
    value: number,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    max: number,
    min: number,
  ) => {
    if (value >= min && value <= max) {
      setValue(value);
    } else if (value > max) {
      setValue(max);
    } else {
      setValue(min);
    }
  };

  const thresholdForUsers = (
    m365: number,
    entra: number,
    salesforce: number,
    d365: number,
    gw: number,
  ) => {
    return (
      m365 > CUSTOMISED_QUOTE ||
      entra > CUSTOMISED_QUOTE ||
      salesforce > CUSTOMISED_QUOTE ||
      d365 > CUSTOMISED_QUOTE ||
      gw > CUSTOMISED_QUOTE
    );
  };

  // override the default behavior of cursor inputs
  useEffect(() => {
    const addListener = (
      id: HTMLElement | null,
      setValue: React.Dispatch<React.SetStateAction<number>>,
      max: number,
      min: number,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e !== null) {
            const element = e.target as HTMLInputElement;
            if (Number.parseInt(element.value) > max) {
              element.value = max.toString();
              setValue(max);
            } else if (!element.value || Number.parseInt(element.value) < min) {
              element.value = min.toString();
              setValue(min);
            }
          }
        });
      }
    };

    addListener(
      document.getElementById('m365-input-for-slider'),
      setM365,
      CloudM365.MAX,
      CloudM365.MIN,
    );

    addListener(
      document.getElementById('entra-input-for-slider'),
      setEntra,
      CloudEntra.MAX,
      CloudEntra.MIN,
    );

    addListener(
      document.getElementById('salesforce-input-for-slider'),
      setSalesforce,
      CloudSalesforce.MAX,
      CloudSalesforce.MIN,
    );

    addListener(
      document.getElementById('d365-input-for-slider'),
      setD365,
      CloudD365.MAX,
      CloudD365.MIN,
    );

    addListener(
      document.getElementById('gw-input-for-slider'),
      setGw,
      CloudGW.MAX,
      CloudGW.MIN,
    );
  }, []);

  return (
    <div className="storage-protect widget-styles">
      <Helmet>
        <script src={urxScript}></script>
        <script src={urxScriptLoader}></script>
      </Helmet>
      {formComplete ? (
        <div className="bx--grid">
          <Thanks
            bpName={bpName}
            bpConsentMail={bpConsentMail}
            m365={m365}
            price={`${totalCost}`}
            advanced={false}
            entra={entra}
            salesforce={salesforce}
            d365={d365}
            gw={gw}
            storage={storage}
          />
        </div>
      ) : (
        <div id="storage-protect-grid">
          {bpName ? (
            <BusinessPartner bpName={bpName} logo={logo} desc={desc} />
          ) : null}
          <div className="bx--row">
            <div
              className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
              id="countryModal"
            >
              {localExist && toggleCountry ? (
                <ModalWrapper
                  modalLabel={`Found country ${modalLocalCountry}. Access page in ${modalLocalLanguage} (${modalLocalCurrency}) `}
                  modalHeading={`The QRadar estimator is available in ${modalLocalLanguage} with local currency ${modalLocalCurrency}`}
                  size={'sm'}
                  primaryButtonText={'Cool! Take me there'}
                  secondaryButtonText={''}
                  buttonTriggerText={`Access page in ${modalLocalLanguage}`}
                  handleOpen={() => {
                    segmentTracking('CTA Clicked', {
                      pageTitle: pageTitle,
                      object: object,
                      resultValue: 'CTA clicked',
                      CTA: 'Modal open ' + modalLocalCountry,
                      location: location,
                      action: '',
                      field: '',
                    });
                  }}
                  handleSubmit={() => {
                    localStorage.setItem('langLocale', selectedLocale);
                    (window.location.href =
                      'https://www.ibm.com/' +
                      selectedLocale +
                      '/products/storage-protect-for-cloud/pricing'),
                      segmentTracking('CTA Clicked', {
                        pageTitle: pageTitle,
                        object: object,
                        resultValue: 'CTA clicked',
                        CTA: 'Switched to country ' + modalLocalCountry,
                        location: location,
                        action: '',
                        field: '',
                      });
                    return true;
                  }}
                ></ModalWrapper>
              ) : null}
            </div>
          </div>
          <div className="bx--row mar3">
            {/* Main content div */}
            <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-4">
              {/* Starting heading */}
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <p className="page_sub_heading">
                    <FormattedMessage {...messages.mainHeader} />
                  </p>
                </div>
              </div>
              {/* offering header with dynamic tooltip content */}
              <div className="bx--row mar3">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                  <p className="page_body_2">
                    <FormattedMessage {...messages.subHeader1} />
                  </p>
                </div>
              </div>
              <div className="bx--row mt-5">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 without-storage">
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Toggle
                        labelA={intl.formatMessage(messages.storageToggle)}
                        labelB={intl.formatMessage(messages.storageToggle)}
                        defaultToggled
                        id="storage-toggle-id"
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: pageTitle,
                            object: object,
                            resultValue: 'CTA clicked',
                            CTA: 'Storage toggle',
                            location: location,
                            action: '',
                            field: '',
                          });
                          setStorage(!storage);
                        }}
                      />
                    </div>
                  </div>
                  <div className="bx--row without-storage-tooltip">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <Tooltip
                        onChange={() => {
                          segmentTracking('CTA Clicked', {
                            pageTitle: pageTitle,
                            object: object,
                            resultValue: 'CTA clicked',
                            CTA: 'Storage toogle tooltip',
                            location: location,
                            action: '',
                            field: '',
                          });
                        }}
                        direction={isSmall ? 'bottom' : 'right'}
                        tabIndex={0}
                      >
                        <span className="slide_text">
                          {storage ? (
                            <FormattedMessage {...messages.toggleTooltip} />
                          ) : (
                            <FormattedMessage {...messages.toggleTooltipOff} />
                          )}
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
              {/* Sliders for selecting the users for M365 */}
              <div>
                <div className="bx--row mar3">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <label className="page_body">
                      <FormattedMessage {...messages.storageM365} />
                    </label>
                    <Tooltip
                      onChange={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C M365 tooltip',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                      direction={isSmall ? 'bottom' : 'right'}
                      tabIndex={0}
                    >
                      <span className="slide_text">
                        <FormattedMessage {...messages.toolTipM365} />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div className="bx--row">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <Slider
                      id="m365"
                      min={CloudM365.MIN}
                      max={CloudM365.MAX}
                      inputType={'number'}
                      value={m365}
                      labelText={<FormattedMessage {...messages.users} />}
                      onBlur={() => {
                        null;
                      }}
                      onChange={({ value }) => {
                        handleSliders(
                          value,
                          setM365,
                          CloudM365.MAX,
                          CloudM365.MIN,
                        );

                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C M365 slider',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Sliders for selecting the users for Entra */}
              <div>
                <div className="bx--row mar3">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <label className="page_body">
                      <FormattedMessage {...messages.storageEntra} />
                    </label>
                    <Tooltip
                      onChange={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C Entra ID tooltip',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                      direction={isSmall ? 'bottom' : 'right'}
                      tabIndex={0}
                    >
                      <span className="slide_text">
                        <FormattedMessage {...messages.toolTipEntraId} />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div className="bx--row">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <Slider
                      id="entra"
                      min={CloudEntra.MIN}
                      max={CloudEntra.MAX}
                      inputType={'number'}
                      value={entra}
                      labelText={<FormattedMessage {...messages.users} />}
                      onBlur={() => {
                        null;
                      }}
                      onChange={({ value }) => {
                        handleSliders(
                          value,
                          setEntra,
                          CloudEntra.MAX,
                          CloudEntra.MIN,
                        );

                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C Entra ID slider',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Sliders for selecting the users for Salesforce */}
              <div>
                <div className="bx--row mar3">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <label className="page_body">
                      <FormattedMessage {...messages.storageSalesforce} />
                    </label>
                    <Tooltip
                      onChange={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C Salesforce ID tooltip',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                      direction={isSmall ? 'bottom' : 'right'}
                      tabIndex={0}
                    >
                      <span className="slide_text">
                        <FormattedMessage {...messages.toolTipSalesforce} />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div className="bx--row">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <Slider
                      id="salesforce"
                      min={CloudSalesforce.MIN}
                      max={CloudSalesforce.MAX}
                      inputType={'number'}
                      value={salesforce}
                      labelText={<FormattedMessage {...messages.users} />}
                      onBlur={() => {
                        null;
                      }}
                      onChange={({ value }) => {
                        handleSliders(
                          value,
                          setSalesforce,
                          CloudSalesforce.MAX,
                          CloudSalesforce.MIN,
                        );

                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C Salesforce ID slider',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Sliders for selecting the users for Dynamics */}
              <div>
                <div className="bx--row mar3">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <label className="page_body">
                      <FormattedMessage {...messages.storageDynamics} />
                    </label>
                    <Tooltip
                      onChange={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C Dynamics tooltip',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                      direction={isSmall ? 'bottom' : 'right'}
                      tabIndex={0}
                    >
                      <span className="slide_text">
                        <FormattedMessage {...messages.toolTipD365} />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div className="bx--row">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <Slider
                      id="d365"
                      min={CloudD365.MIN}
                      max={CloudD365.MAX}
                      inputType={'number'}
                      value={d365}
                      labelText={<FormattedMessage {...messages.users} />}
                      onBlur={() => {
                        null;
                      }}
                      onChange={({ value }) => {
                        handleSliders(
                          value,
                          setD365,
                          CloudD365.MAX,
                          CloudD365.MIN,
                        );

                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C Dynamics slider',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Sliders for selecting the users for GW */}
              <div>
                <div className="bx--row mar3">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <label className="page_body">
                      <FormattedMessage {...messages.storageGW} />
                    </label>
                    <Tooltip
                      onChange={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C GW tooltip',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                      direction={isSmall ? 'bottom' : 'right'}
                      tabIndex={0}
                    >
                      <span className="slide_text">
                        <FormattedMessage {...messages.toolTipGW} />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div className="bx--row">
                  <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                    <Slider
                      id="gw"
                      min={CloudGW.MIN}
                      max={CloudGW.MAX}
                      inputType={'number'}
                      value={gw}
                      labelText={<FormattedMessage {...messages.users} />}
                      onBlur={() => {
                        null;
                      }}
                      onChange={({ value }) => {
                        handleSliders(value, setGw, CloudGW.MAX, CloudGW.MIN);

                        segmentTracking('CTA Clicked', {
                          pageTitle: pageTitle,
                          object: object,
                          resultValue: 'CTA clicked',
                          CTA: 'SP4C GW slider',
                          location: location,
                          action: '',
                          field: '',
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
              <div id="footnote" className="div-only-lg mar3">
                <div className="bx--row">
                  <div className="bx--col-lg-16">
                    <Footnote numNote={1} />
                    <div className="footnote_text">
                      <DisclaimerText
                        localeCode={localeCode}
                        product={'Storage Protect for Cloud'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Separator div */}
            <div className="bx--col-lg-2 bx--col-md-16 bx--col-sm-16"></div>
            {/* Pricing/Greybox div */}
            <div className="bx--col-lg-5 bx--col-md-16 bx--col-sm-16 summary-section">
              <div className="bx--row">
                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 summary-pricing">
                  {/* Table Header */}
                  <div className="bx--row">
                    <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body bold">
                        <FormattedMessage {...messages.configuration} />
                      </p>
                    </div>
                    <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body bold ">
                        <FormattedMessage {...messages.pricing} />
                      </p>
                    </div>
                  </div>
                  {/* Microsoft 265 */}
                  <div>
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <hr className="storage-protect__gray-hr" />
                      </div>
                    </div>
                    <div className="bx--row">
                      <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                        <p className="price_table_comp__requirement price_table_comp_color">
                          <FormattedMessage {...messages.storageM365} />
                        </p>
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-2">
                        <p className="page_body_2">{m365Price}</p>
                      </div>
                    </div>
                  </div>

                  {/* Entra Id */}
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="storage-protect__gray-hr" />
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                      <p className="price_table_comp__requirement price_table_comp_color">
                        <FormattedMessage {...messages.storageEntra} />
                      </p>
                    </div>
                    <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-2">
                      <p className="page_body_2">{entraPrice}</p>
                    </div>
                  </div>
                  {/* Cloud Salesforce */}
                  <div>
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <hr className="storage-protect__gray-hr" />
                      </div>
                    </div>
                    <div className="bx--row">
                      <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                        <p className="price_table_comp__requirement price_table_comp_color">
                          <FormattedMessage {...messages.storageSalesforce} />
                        </p>
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-2">
                        <p className="page_body_2">{sfPrice}</p>
                      </div>
                    </div>
                  </div>
                  {/* Cloud Dynamics 365 */}
                  <div>
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <hr className="storage-protect__gray-hr" />
                      </div>
                    </div>
                    <div className="bx--row">
                      <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                        <p className="price_table_comp__requirement price_table_comp_color">
                          <FormattedMessage {...messages.storageDynamics} />
                        </p>
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-2">
                        <p className="page_body_2">{d365Price}</p>
                      </div>
                    </div>
                  </div>
                  {/* Google Workspace */}
                  <div>
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <hr className="storage-protect__gray-hr" />
                      </div>
                    </div>
                    <div className="bx--row">
                      <div className="bx--col-lg-10 bx--col-md-16 bx--col-sm-2">
                        <p className="price_table_comp__requirement price_table_comp_color">
                          <FormattedMessage {...messages.storageGW} />
                        </p>
                      </div>
                      <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-2">
                        <p className="page_body_2">{gwPrice}</p>
                      </div>
                    </div>
                  </div>
                  {/* Storage */}
                  <div>
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <hr className="storage-protect__gray-hr" />
                      </div>
                    </div>
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-2">
                        <p className="price_table_comp__requirement price_table_comp_color">
                          {storage ? (
                            <FormattedMessage
                              {...messages.withUnlimitedStorageToggleOn}
                            />
                          ) : (
                            <FormattedMessage
                              {...messages.withUnlimitedStorageToggleOff}
                            />
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      <hr className="storage-protect__gray-hr" />
                    </div>
                  </div>
                  {!thresholdForUsers(m365, entra, salesforce, d365, gw) ? (
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p
                          id="widget.estimated-cost"
                          className="storage-protect__ibm-type-e price_table_comp__bodycopy padding_24_2"
                        >
                          <FormattedMessage {...messages.estimatedMonthy} />
                          <sup className="storage-protect__sup">1</sup>
                        </p>
                        <p className="padding_8 price_table_comp__price">
                          {totalCostRaw === 0 ? (
                            <SkeletonPlaceholder className="storage-protect__sticky-nav-skeleton" />
                          ) : (
                            `${totalCost} *`
                          )}
                        </p>
                        <p className="price_table_comp__disclaimer price_table_comp_color padding_24_1">
                          * <FormattedMessage {...messages.asteriskPrice} />
                        </p>
                      </div>
                    </div>
                  ) : null}
                  <div className="bx--row">
                    <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                      {!thresholdForUsers(m365, entra, salesforce, d365, gw) ? (
                        <Button
                          id="requestQuote"
                          renderIcon={ArrowRight16}
                          kind="primary"
                          className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16"
                          onClick={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: pageTitle,
                              object: object,
                              resultValue: 'CTA clicked',
                              CTA: 'Request a quote now',
                              location: location,
                              action: '',
                              field: '',
                            });
                            initUrxForm(
                              environment,
                              URX_FORM.INSTANCE_ID,
                              URX_FORM.FORM_ID,
                              urxlang as IBMLocale,
                              companyId,
                              shortcode,
                              setFormId,
                            );
                            setDrawerOpen(true);
                            setCookie();
                          }}
                        >
                          <FormattedMessage {...messages.requestQuote} />
                        </Button>
                      ) : (
                        <Button
                          id="customisedQuote"
                          renderIcon={Edit16}
                          kind="primary"
                          className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16"
                          onClick={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: pageTitle,
                              object: object,
                              resultValue: 'CTA clicked',
                              CTA: 'Get a cutomized quote',
                              location: location,
                              action: '',
                              field: '',
                            });
                            initUrxForm(
                              environment,
                              URX_FORM.INSTANCE_ID,
                              URX_FORM.FORM_ID,
                              urxlang as IBMLocale,
                              companyId,
                              shortcode,
                              setFormId,
                            );
                            setDrawerOpen(true);
                            setCookie();
                          }}
                        >
                          <FormattedMessage {...messages.customizedQuote} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FormModal
            pageTitle={pageTitle}
            object={object}
            productTitle={productTitle}
            pageUrl={pageUrl}
            location={location}
            bpName={bpName}
            logo={logo}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            formId={formId}
            lang={lang}
          ></FormModal>
        </div>
      )}
    </div>
  );
};

export default StorageProtectCalculator;
